import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Globe, Check } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { languageNames, languageFlags } from "@/i18n/translations";

const LanguageSwitcher = ({ variant = "default" }) => {
  const { language, changeLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const languages = Object.keys(languageNames);

  const buttonClass = variant === "light" 
    ? "text-white hover:text-marine-300" 
    : "text-navy-900 hover:text-marine-600";

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="language-switcher"
        className={`flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${buttonClass}`}
        aria-label="Change language"
      >
        <Globe className="w-4 h-4" />
        <span className="hidden sm:inline">{languageFlags[language]}</span>
        <span className="uppercase">{language}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-48 bg-white shadow-lg border border-slate-100 z-50 max-h-80 overflow-y-auto"
          >
            {languages.map((lang) => (
              <button
                key={lang}
                onClick={() => {
                  changeLanguage(lang);
                  setIsOpen(false);
                }}
                data-testid={`lang-${lang}`}
                className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-left transition-colors hover:bg-slate-50 ${
                  language === lang ? "bg-slate-50 text-marine-600" : "text-navy-900"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-base">{languageFlags[lang]}</span>
                  <span>{languageNames[lang]}</span>
                </div>
                {language === lang && <Check className="w-4 h-4" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;
